import { useState } from 'react';
import { Eye, FileText, IdCard } from 'lucide-react';
import type { DocumentType } from '@kmdx/contracts';
import { DOCUMENT_TYPE_BG } from '@/i18n';
import { formatDate } from '@/lib/format';
import { useDocumentsOf } from '@/api/hooks/documents';
import type { DocumentSummary } from '@/api/types';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Select } from '@/components/ui/Field';
import { UploadButton } from '@/components/documents/DocSet';

/** Какво още се пази към бус освен талона. */
const OTHER_TYPES: DocumentType[] = ['insurance', 'vignette', 'vehicle_document', 'other'];

/**
 * ТАЛОНЪТ И ДОКУМЕНТИТЕ НА БУСА. Клиентът: „копие на талона, снимано в
 * Бусове". Снимката е под ръка, когато трябва VIN, F.1/F.2/G или номерът на
 * рамата — без да се търси хартията в кабината. Снима се лицето и гърбът.
 */
export function VehicleDocumentsCard({
  vehicleId,
  missingRegistrationData,
}: {
  vehicleId: string;
  /** F.2 или G още не са въведени — талонът е мястото, откъдето се взимат. */
  missingRegistrationData: boolean;
}) {
  const files = useDocumentsOf({ vehicleId });
  const [otherType, setOtherType] = useState<DocumentType>('insurance');
  const items = (files.data?.items ?? []).filter((d) => d.status !== 'superseded');
  const talon = items.filter((d) => d.type === 'vehicle_registration');
  const other = items.filter((d) => d.type !== 'vehicle_registration');

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <IdCard className="size-4 text-ink-dim" />
            Талон и документи на буса
          </span>
        }
        subtitle={talon.length ? `Талон: ${talon.length} ${talon.length === 1 ? 'снимка' : 'снимки'}` : 'Талонът още не е качен'}
        actions={<UploadButton type="vehicle_registration" target={{ vehicleId }} label={talon.length ? 'Още страница' : 'Качи талона'} accept="image/*,application/pdf" />}
      />
      <CardBody className="space-y-3">
        {talon.length === 0 ? (
          <p className="text-xs text-ink-dim">
            Снимайте лицето и гърба на талона с телефона и ги качете тук — оттам се взимат F.2 (допустима маса) и G (маса
            в експлоатация), от които се смята колко товар носи бусът.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {talon.map((d) => (
              <Thumb key={d.id} document={d} />
            ))}
          </div>
        )}
        {missingRegistrationData ? (
          <p className="rounded border border-warn/40 bg-warn-soft/40 px-2 py-1.5 text-xs text-warn">
            Попълнете F.2 и G от талона в „Данни на буса" — без тях товароносимостта е ръчна или липсва.
          </p>
        ) : null}

        <div className="border-t border-line pt-3">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-ink-faint">Други документи</span>
            <span className="ml-auto flex items-center gap-1.5">
              <Select
                className="h-8 w-40 text-xs"
                aria-label="Вид документ"
                value={otherType}
                onChange={(e) => setOtherType(e.target.value as DocumentType)}
                options={OTHER_TYPES.map((t) => ({ value: t, label: DOCUMENT_TYPE_BG[t] ?? t }))}
              />
              <UploadButton type={otherType} target={{ vehicleId }} label="Качи" />
            </span>
          </div>
          {other.length === 0 ? (
            <p className="text-xs text-ink-faint">Застраховка, винетка, документи от сервиз — качват се тук.</p>
          ) : (
            <ul className="divide-y divide-line">
              {other.map((d) => (
                <li key={d.id} className="flex items-center gap-2 py-1.5 text-sm">
                  <FileText className="size-3.5 shrink-0 text-ink-dim" />
                  <span className="font-medium">{DOCUMENT_TYPE_BG[d.type] ?? d.type}</span>
                  <span className="min-w-0 flex-1 truncate text-xs text-ink-faint">
                    {d.fileName} · {formatDate(d.createdAt)}
                  </span>
                  {d.previewUrl ? (
                    <a href={d.previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-accent hover:underline">
                      <Eye className="size-3.5" />
                      отвори
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/** Малка снимка, която се отваря цяла в нов раздел. PDF-ът е икона. */
function Thumb({ document }: { document: DocumentSummary }) {
  const isImage = document.mimeType.startsWith('image/');
  return (
    <a
      href={document.previewUrl ?? undefined}
      target="_blank"
      rel="noreferrer"
      className="group block w-36 overflow-hidden rounded-lg border border-line bg-raised hover:border-accent/60"
      title={`${document.fileName} · ${formatDate(document.createdAt)}`}
    >
      {isImage && document.previewUrl ? (
        <img src={document.previewUrl} alt="Талон" className="h-24 w-full object-cover" />
      ) : (
        <div className="flex h-24 items-center justify-center text-ink-dim">
          <FileText className="size-8" />
        </div>
      )}
      <div className="truncate px-2 py-1 text-[11px] text-ink-dim group-hover:text-accent">{formatDate(document.createdAt)}</div>
    </a>
  );
}
